import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { secureFetch } from "../utils/secureFetch";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../utils/analytics";

const API_BASE = process.env.REACT_APP_API_BASE;

const isFineTransaction = (transaction) => {
  const type = String(transaction?.type || "").toLowerCase();
  const description = String(transaction?.description || "").trim().toLowerCase();
  return type === "fine" || description.startsWith("fine:");
};

const isSpendTransaction = (transaction) =>
  Number(transaction?.amount) < 0 && !isFineTransaction(transaction);

const saveCsv = async (response, fileName, shareTitle) => {
  if (Capacitor.isNativePlatform()) {
    const csv = await response.text();
    const savedFile = await Filesystem.writeFile({
      path: fileName,
      data: csv,
      directory: Directory.Cache,
      encoding: Encoding.UTF8
    });

    await Share.share({
      title: shareTitle,
      text: shareTitle,
      files: [savedFile.uri],
      dialogTitle: `Save or share ${fileName}`
    });
    return;
  }

  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(downloadUrl);
};

export default function AdminDashboard() {

  const navigate = useNavigate();
  const email = localStorage.getItem("beercard_logged_in_email");
  const role = localStorage.getItem("beercard_role");
  const [forbidden, setForbidden] = useState(false);
  const [selectedDrinkForUser, setSelectedDrinkForUser] = useState({});
  const [activeUsers, setActiveUsers] = useState([]);
  const [shopPurchase, setShopPurchase] = useState({drinkName: '',quantity: '',cost: ''});
  const [shopPurchases, setShopPurchases] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [teamId, setTeamId] = useState(localStorage.getItem("beercard_teamId") || null);
  const [clubId, setClubId]   = useState(localStorage.getItem("beercard_clubId")   || null);
  const [txs, setTxs] = useState([]);
  const [filterEmail, setFilterEmail] = useState("");     // selected player (or all)
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);

  // Support both new (teamId/clubId) and legacy (team/club) fields
  const sameClubTeam = (u) => (u.clubId === clubId || u.club === clubId) && (u.teamId === teamId || u.team === teamId);

  // Format date in dd/mm/yyyy HH:MM GMT±X using Australia/Sydney
const formatSydneyDate = (dateLike) => {
  const d = new Date(dateLike);
  // "shortOffset" gives "GMT+10" / "GMT+11" automatically (handles DST)
  const formatted = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'shortOffset'
  }).format(d);
  // Typically returns: "01/10/2025, 14:30 GMT+11" — remove the comma for neatness
  return formatted.replace(',', '');
};

  // Check role immediately
  useEffect(() => {
    if (!email) {
      alert("❌ You must be logged in to access the admin dashboard.");
      navigate("/");  // or your login route
      return;
    }
  
    if (role !== "admin") {
      console.warn("❌ Forbidden: Not admin");
      setForbidden(true);
    }
  }, [role, email, navigate]);  

  const [players, setPlayers] = useState([]);
  const [fineTypes, setFineTypes] = useState([]);
  const [selectedFineTypeId, setSelectedFineTypeId] = useState("");
  const [balances, setBalances] = useState({});
  const [playersFines, setPlayersFines] = useState({});
  const [search, setSearch] = useState('');
  const [topups, setTopups] = useState([]);
  const [message, setMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState("");
  const [weeklyTransactions, setWeeklyTransactions] = useState([]);

  // Function to get transactions for the current week, scoped to this admin's team/club
  const getCurrentWeekTransactions = (transactions) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
    endOfWeek.setHours(23, 59, 59, 999);

    return (transactions || [])
      .filter(tx => {
        if (!tx.createdAt && !tx.date) return false;

        const txDate = new Date(tx.createdAt || tx.date);
        if (Number.isNaN(txDate.getTime())) return false;

        const matchesTeamClub = (!teamId || tx.teamId === teamId) && (!clubId || tx.clubId === clubId);
        return matchesTeamClub && txDate >= startOfWeek && txDate <= endOfWeek;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.date).getTime() || 0;
        const dateB = new Date(b.createdAt || b.date).getTime() || 0;
        return dateB - dateA; // Most recent first
      });
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // If IDs aren’t known yet, fetch profile and hydrate BOTH LS and state
 useEffect(() => {
   (async () => {
     if (!clubId || !teamId) {
       try {
         const me = await secureFetch(`${API_BASE}/user`);
         const resolvedClub = me?.clubId || me?.club || null;
         const resolvedTeam = me?.teamId || me?.team || null;
         if (resolvedClub) { localStorage.setItem("beercard_clubId", resolvedClub); setClubId(resolvedClub); }
         if (resolvedTeam) { localStorage.setItem("beercard_teamId", resolvedTeam); setTeamId(resolvedTeam); }
       } catch (e) {
         console.warn("Could not load /user to hydrate IDs:", e);
       }
     }
   })();
 }, []);

  const loadFineTypes = async () => {
    if (!teamId || !clubId) return;

    try {
      // Backend now resolves the club sport and returns the correct defaults
      // plus any custom/overridden fines for this exact team/club.
      const fines = await secureFetch(
        `${API_BASE}/fine-types?teamId=${encodeURIComponent(teamId)}&clubId=${encodeURIComponent(clubId)}`
      );

      setFineTypes(
        (fines || []).sort((a, b) =>
          String(a.description || "").localeCompare(String(b.description || ""))
        )
      );
    } catch (err) {
      console.error("❌ Failed to load fine types", err);
    }
  };

  useEffect(() => {
    loadFineTypes();
  }, [teamId, clubId]);
  
  useEffect(() => {
  const fetchPurchases = async () => {
      try {
        if (!teamId || !clubId) return; // wait until IDs are known
        const res = await secureFetch(`${API_BASE}/shop-purchases?teamId=${teamId}&clubId=${clubId}`);
        setShopPurchases(res || []);
      } catch (err) {
        console.error("❌ Failed to fetch purchases:", err);
      }
    };

    fetchPurchases();
  }, [teamId, clubId]);


  const loadDashboardData = async () => {
    try {
      // Fetch users
      const usersRes = await secureFetch(`${API_BASE}/users?teamId=${teamId}&clubId=${clubId}`);
      // ✅ Only show approved users in THIS admin's club & team
      const scopedActive = usersRes.filter(
       (u) => u.status === "approved" && sameClubTeam(u)
     );
     setPlayers(scopedActive);
  
      // Fetch balances (one at a time)
      const balances = {};
      // ✅ Only fetch balances for scoped active users
      for (const user of scopedActive) {
        const balRes = await secureFetch(`${API_BASE}/balance/${user.email}`);
        balances[user.email] = { balance: balRes.balance ?? 0 };
      }
      setBalances(balances);

      console.log("✅ Live balances loaded from DynamoDB:", balances);

      // Fetch fines summary
      const fines = await secureFetch(
        `${API_BASE}/fines/summary?teamId=${encodeURIComponent(teamId)}&clubId=${encodeURIComponent(clubId)}`
      );
      const fineTotals = {};
      fines.forEach(f => {
        fineTotals[f.email] = (fineTotals[f.email] || 0) + (f.amount || 0);
      });
      setPlayersFines(fineTotals);
  
      // Fetch all transactions
      const txsAll = await secureFetch(
      `${API_BASE}/transactions/all?teamId=${encodeURIComponent(teamId)}&clubId=${encodeURIComponent(clubId)}`
      );
      setTxs(txsAll);
      const topups = txsAll.filter(tx => tx.amount > 0);
      setTopups(topups);
      // Filter and set weekly transactions
      const weeklyTxs = getCurrentWeekTransactions(txsAll).filter(
        tx => !tx.description?.toLowerCase().includes("bottle shop") && !tx.description?.toLowerCase().includes("stocked ")
      );
      setWeeklyTransactions(weeklyTxs);

    } catch (err) {
      console.error("❌ Failed to load dashboard data:", err);
    }
  };  
 
  useEffect(() => {
  if (clubId && teamId) loadDashboardData();
 }, [clubId, teamId]);
  
    const [newDrink, setNewDrink] = useState({ name: '', price: '', stock: '', type: 'Beer' });
    const [drinkStock, setDrinkStock] = useState([]);

    useEffect(() => {
   if (!clubId || !teamId) return;
   secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`)
     .then(setDrinkStock)
     .catch(err => console.error("❌ Failed to load drinks:", err));
 }, [clubId, teamId]);

  // Define thresholds
    const LOW_STOCK_LIMIT = 3;

    const resetAllBalances = async () => {
      if (!window.confirm("Are you sure you want to reset all balances?")) return;
    
      for (const player of players) {
        await secureFetch(`${API_BASE}/topup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: player.email, amount: -balances[player.email]?.balance ?? 0 })
        });
      }
    
      // Refresh balances
      const updated = {};
      for (const player of players) {
        const { balance } = await secureFetch(`${API_BASE}/balance/${player.email}`);
        updated[player.email] = { balance };
      }
      setBalances(updated);
    
      alert("✅ All balances reset.");
    };    

    const handleDeleteDrink = async (drinkName) => {
  if (!window.confirm(`Delete '${drinkName}'? This cannot be undone.`)) return;

  try {
    const res = await secureFetch(`${API_BASE}/drinks/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ teamId, name: drinkName, safe: true }) // set safe:false to force delete
    });

    if (res.success) {
      const updated = await secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`);
      setDrinkStock(updated);
      alert(`🗑️ Deleted ${drinkName}`);
    } else {
      alert(`❌ ${res.error || "Failed to delete drink"}`);
    }
  } catch (err) {
    console.error("❌ Delete error:", err);
    alert("Server error deleting drink");
  }
};

const handleExportShopPurchases = async () => {
  try {
    if (!teamId || !clubId) {
      alert("Missing team or club. Please refresh and try again.");
      return;
    }

    const resp = await fetch(`${API_BASE}/shop-purchases.csv?teamId=${teamId}&clubId=${clubId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("beercard_token")}`,
        "x-user-email": email || ""
      }
    });

    if (!resp.ok) {
      throw new Error(`CSV export failed (${resp.status}): ${await resp.text()}`);
    }

    await saveCsv(
      resp,
      `shop-purchases_${clubId}_${teamId}.csv`,
      "TeamKitty bottle shop purchases"
    );
    trackEvent("admin_export_shop_purchases");
  } catch (e) {
    console.error("❌ Failed to export shop purchases:", e);
    alert("Failed to export CSV.");
  }
};

 const handleAdminPurchase = async (email) => {
  const drinkName = selectedDrinkForUser[email];
  if (!drinkName || drinkName.trim() === "") {
    alert("Please select a drink");
    return;
  }

  try {
    console.log("Admin purchase payload:", { email, drinkName, adminOverride: true, teamId, clubId });
    const res = await secureFetch(`${API_BASE}/drinks/redeem`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, drinkName, adminOverride: true, teamId, clubId})
    });

    if (res.success) {
  alert(`✅ Redeem ${drinkName} for ${email}`);
  trackEvent("admin_redeem_drink", { item_name: drinkName });

  // ✅ Update the displayed balance using backend value
  setActiveUsers((prev) =>
    prev.map((user) =>
      user.email === email
        ? { ...user, balance: res.newBalance }
        : user
    )
  );

  // ✅ Re-fetch drink stock
  const updatedDrinks = await secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`);
  setDrinkStock(updatedDrinks);

  // ✅ Reset dropdown
  setSelectedDrinkForUser((prev) => ({
    ...prev,
    [email]: ""
  }));

  // Update active users or balances state after action
await loadDashboardData();  // re-fetches all users and drink stock

}

     else {
      alert("❌ Redeem failed.");
    }
  } catch (err) {
    console.error("❌ Error in redeem:", err);
    alert("Something went wrong");
  }
};

  const exportTransactions = async () => {
  try {
    if (!teamId || !clubId) {
      alert("Missing team or club. Please refresh and try again.");
      return;
    }

    const token = localStorage.getItem("beercard_token");

    const url = `${API_BASE}/transactions/all.csv?teamId=${encodeURIComponent(teamId)}&clubId=${encodeURIComponent(clubId)}`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-user-email": email || ""
      }
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`CSV export failed (${res.status}): ${errText}`);
    }

    await saveCsv(
      res,
      `transactions_${clubId}_${teamId}.csv`,
      "TeamKitty transactions"
    );
    trackEvent("admin_export_transactions");
  } catch (err) {
    console.error("❌ Failed to export transactions:", err);
    alert("Failed to export transactions");
  }
};
  
  const handleAddDrink = async () => {
    if (!newDrink.name || !newDrink.price || !newDrink.stock || !newDrink.type) {
      alert("Please fill out all fields");
      return;
    }
  
    try {
      console.log(process.env.REACT_APP_API_BASE);

      const data = await secureFetch(`${API_BASE}/drinks/add?teamId=${teamId}&clubId=${clubId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newDrink.name,
          type: newDrink.type,
          price: parseFloat(newDrink.price),
          stock: parseInt(newDrink.stock),
          teamId,
          clubId
        })
      });
  
      if (data.message) {
        alert(`✅ ${data.message}`);
        trackEvent("admin_add_drink", {
          item_category: newDrink.type,
          value: parseFloat(newDrink.price) || 0,
          currency: "AUD"
        });
  
        // Refresh drinks list
        const updatedDrinks = await secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`);
        setDrinkStock(updatedDrinks);
      } else {
        alert(`❌ ${data.error || "Something went wrong"}`);
      }
    } catch (err) {
      console.error("❌ Failed to add drink:", err);
      alert("❌ Could not connect to the server");
    }
  
    // Clear form
    setNewDrink({ name: '', price: '', stock: '', type: 'Beer' });
  };  

  const handleBottleShopPurchase = async () => {
  const { drinkName, quantity, totalCost, shopName } = shopPurchase;

  if (!drinkName || !quantity) {
    alert("❌ Please enter both drink name and quantity.");
    return;
  }

  try {
    const email = localStorage.getItem("beercard_logged_in_email");
    const addedByEmail = email;

    const payload = {
      email,
      drinkName,
      quantity: Number(quantity),
      totalCost: totalCost ? Number(totalCost) : undefined,
      shopName: shopName?.trim() || undefined,
      addedByEmail,
      teamId,
      clubId
    };

    const res = await secureFetch(`${API_BASE}/drinks/admin-add-stock?teamId=${teamId}&clubId=${clubId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (res.success) {
      alert(`✅ Logged ${quantity} x ${drinkName}`);
      trackEvent("admin_log_bottle_shop_purchase", {
        item_name: drinkName,
        quantity: Number(quantity),
        value: totalCost ? Number(totalCost) : undefined,
        currency: "AUD"
      });

      // reset using totalCost, not cost
      setShopPurchase({
        drinkName: "",
        quantity: "",
        totalCost: "",
        shopName: ""
      });

      // refresh stock
      const updatedDrinks = await secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`);
      setDrinkStock(updatedDrinks);

      // refresh bottle shop purchases
      await loadShopPurchases();
    } else {
      alert("❌ Failed to log purchase");
    }
  } catch (err) {
    console.error("❌ Bottle shop log error:", err);
    alert("Server error");
  }
};

const loadShopPurchases = async () => {
  if (!teamId || !clubId) return;
  setIsLoadingPurchases(true);
  try {
    const url = `${API_BASE}/shop-purchases?teamId=${teamId}&clubId=${clubId}` +
                (filterEmail ? `&email=${encodeURIComponent(filterEmail)}` : "");
    const items = await secureFetch(url);
    items.sort((a,b) => (b.date || '').localeCompare(a.date || ''));
    setShopPurchases(items || []);
  } catch (e) {
    console.error("❌ Failed to load purchases:", e);
  } finally {
    setIsLoadingPurchases(false);
  }
};

  const updateBalance = async (email, amount) => {
    try {
      const data = await secureFetch(`${API_BASE}/admin/topup`, {
        method: "POST",
        body: JSON.stringify({ email, amount }),
        headers: { "Content-Type": "application/json" }
      });  
  
      if (data.success) {
        // Update immediately
        //setBalances(prev => ({ ...prev, [email]: { balance: data.newBalance } }));
  
        // Show confirmation
        alert(`✅ Balance updated to $${data.newBalance}`);

        // Optionally refresh everything in background
        loadDashboardData(); // don't await
        }
      else {
        alert(`❌ ${data.error || "Something went wrong while updating the balance."}`);
      }
    } catch (err) {
      alert("❌ Failed to top up");
      console.error(err);
    }
  };  

  const handlePostMessage = () => {
    localStorage.setItem("beercard_message", message);
    setSavedMessage(message);
    setMessage('');
  }

  const undoLastTransaction = (email) => {
    const txs = JSON.parse(localStorage.getItem("beercard_transactions") || "{}");
    const userTxs = txs[email] || [];
    if (userTxs.length === 0) return;

    const [last, ...rest] = userTxs;
    const newBal = (balances[email]?.balance || 0) - last.amount;
    const newTxs = { ...txs, [email]: rest };

    localStorage.setItem("beercard_transactions", JSON.stringify(newTxs));
    setBalances(prev => ({ ...prev, [email]: { balance: newBal } }));
  };

  const handleExportFinesCsv = async () => {
  try {
    if (!teamId || !clubId) {
      alert("Missing team/club context");
      return;
    }
    const url = `${API_BASE}/fines.csv?teamId=${teamId}&clubId=${clubId}` + (filterEmail ? `&email=${encodeURIComponent(filterEmail)}` : "");
    const resp = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("beercard_token")}`,
        "x-user-email": email || ""
      }
    });
    if (!resp.ok) {
      throw new Error(`❌ ${resp.status} : ${await resp.text()}`);
    }
    await saveCsv(
      resp,
      `fines_${clubId}_${teamId}${filterEmail ? `_${filterEmail}` : "_all"}.csv`,
      "TeamKitty fines"
    );
    trackEvent("admin_export_fines");
  } catch (e) {
    console.error("❌ Failed to export fines CSV:", e);
    alert("Failed to export fines CSV.");
  }
};

  const [pendingUsers, setPendingUsers] = useState([]);

  useEffect(() => {
   if (!clubId || !teamId) return;
   secureFetch(`${API_BASE}/users/pending?teamId=${teamId}&clubId=${clubId}`)
     .then((pending) => setPendingUsers(pending.filter(sameClubTeam)))
     .catch(err => console.error("❌ Failed to load pending users:", err));
 }, [clubId, teamId]);

const approveUser = async (email) => {
  try {
    await secureFetch(`${API_BASE}/users/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    setPendingUsers(prev => prev.filter(u => u.email !== email));
    alert(`✅ Approved ${email}`);
    await loadDashboardData();
  } catch (err) {
    console.error(`❌ Failed to approve ${email}:`, err);
  }
};

const deactivateUser = async (email) => {
  if (!window.confirm(`Are you sure you want to deactivate ${email}?`)) return;

  try {
    await secureFetch(`${API_BASE}/users/deactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });

    alert(`✅ Deactivated ${email}`);
    await loadDashboardData(); // reload the active user list
  } catch (err) {
    console.error(`❌ Failed to deactivate ${email}:`, err);
    alert("❌ Something went wrong. Please try again.");
  }
};

// Players currently visible in the table (after search + approved)
const displayedPlayers = players
  .filter(p => p.status === "approved")
  .filter(p =>
    (p.name && p.name.includes(search)) ||
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
    p.email.includes(search)
  );

// Helper: alcohol spend for a single player (scoped to team/club)
const playerAlcoholSpend = (playerEmail) =>
  (txs || [])
    .filter(t =>
      t.email === playerEmail &&
      t.teamId === teamId && t.clubId === clubId &&
      isSpendTransaction(t) &&
      !t.description?.includes("Top-up via Stripe") &&
      !t.description?.includes("Admin balance update")
    )
    .reduce((s, t) => s + Math.abs(t.amount), 0);

// Totals for the displayed rows
const totalAlcoholSpendAll = displayedPlayers.reduce(
  (sum, p) => sum + playerAlcoholSpend(p.email),
  0
);

const totalFinesAll = displayedPlayers.reduce(
  (sum, p) => sum + (playersFines?.[p.email] || 0),
  0
);

const totalBalanceAll = displayedPlayers.reduce(
  (sum, p) => sum + Number(balances[p.email]?.balance || 0),
  0
);

// Sum bottle shop spend by drink (already scoped by team/club via your fetch)
const spendByDrink = React.useMemo(() => {
  const map = {};
  (shopPurchases || []).forEach(p => {
    if (typeof p.totalCost === "number") {
      map[p.drinkName] = (map[p.drinkName] || 0) + p.totalCost;
    }
  });
  return map;
}, [shopPurchases]);

// Grand total across the drinks currently shown in the table
const grandTotalBottleShopSpent = React.useMemo(() => {
  return (drinkStock || []).reduce(
    (sum, d) => sum + (spendByDrink[d.name] || 0),
    0
  );
}, [drinkStock, spendByDrink]);

// Total Cost for the currently loaded bottle shop purchases (scoped via teamId/clubId fetch)
const totalBottleShopCostShown = React.useMemo(() => {
  return (shopPurchases || []).reduce((sum, p) => {
    const v = Number(p.totalCost);
    return Number.isFinite(v) ? sum + v : sum;
  }, 0);
}, [shopPurchases]);


if (forbidden) {
return (
     <div className="min-h-screen w-full bg-[var(--tk-navy)] text-white">
       <div className="w-full max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white text-slate-900 rounded-xl shadow-md p-6">
          <h1 className="text-3xl font-bold mb-4 text-red-600">403 Forbidden</h1>
          <p className="text-gray-700">You do not have permission to access this page.</p>
          <button
            onClick={() => navigate("/player")}
            className="mt-6 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go to Player Dashboard
          </button>
        </div>
       </div>
      </div>
    );
  } else 
  return (
    <div className="min-h-screen w-full bg-[var(--tk-navy)] text-white">
      <div className="w-full max-w-4xl px-3 sm:px-6 py-6 mx-auto">
      <div className="mb-4">
      {drinkStock.filter(d => d.stock === 0).map(d => (
  <div key={d.name} className="bg-red-100 text-red-800 p-2 rounded mb-1 text-lg">
    ❌ <strong>{d.name}</strong> is out of stock!
  </div>
))}
{drinkStock.filter(d => d.stock > 0 && d.stock <= LOW_STOCK_LIMIT).map(d => (
  <div key={d.name} className="bg-yellow-100 text-yellow-800 p-2 rounded mb-1 text-lg">
    ⚠️ <strong>{d.name}</strong> is running low (only {d.stock} left)
  </div>
))}
</div>
      <h2 className="text-2xl font-bold mb-4">Admin Dashboard</h2>
     {pendingUsers.length > 0 && (
  <>
  
    <h3 className="text-xl font-bold mt-10 mb-3">Pending Users</h3>
    <div className="overflow-x-auto">
    <table className="w-full text-sm sm:text-base border mb-6">
      <thead>
        <tr>
          <th className="p-2">Name</th>
          <th className="p-2">Email</th>
          <th className="p-2">Action</th>
        </tr>
      </thead>
      <tbody>
        {pendingUsers.map(user => (
          <tr key={user.email}>
            <td className="p-2">{user.firstName} {user.lastName}</td>
            <td className="p-2">{user.email}</td>
            <td className="p-2">
              <button
                className="px-3 py-1 bg-green-600 text-white rounded"
                onClick={() => approveUser(user.email)}
              >
                Approve
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  </>
)}
<div className="my-4" />
<input type="text" placeholder="Search active players..." value={search} onChange={(e) => setSearch(e.target.value)} className="p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white" />
<h3 className="text-xl font-bold mt-10 mb-3">Active Users</h3>

{isMobile ? (
  <div className="grid gap-3 mb-6">

    {players
      .filter(p => p.status === "approved")
      .filter(p =>
        (p.name && p.name.includes(search)) ||
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
        p.email.includes(search)
      )
      .map(p => {

        const spend = txs
          .filter(t =>
            t.email === p.email &&
            t.teamId === teamId &&
            t.clubId === clubId &&
            isSpendTransaction(t) &&
            !t.description?.includes("Top-up via Stripe") &&
            !t.description?.includes("Admin balance update")
          )
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);

        return (
          <div
            key={p.email}
            className="bg-white text-slate-900 rounded-xl shadow-md p-4"
          >

            {/* HEADER */}
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold">
                  {p.firstName} {p.lastName}
                </div>
                <div className="text-xs text-gray-500 break-all">
                  {p.email}
                </div>
              </div>

              <div className="text-right text-sm">
                <div>💰 ${balances[p.email]?.balance ?? 0}</div>
              </div>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div className="bg-gray-100 rounded p-2">
                <div className="text-gray-500">Spend</div>
                <div className="font-semibold">${spend.toFixed(2)}</div>
              </div>

              <div className="bg-gray-100 rounded p-2">
                <div className="text-gray-500">Fines</div>
                <div className="font-semibold">
                  ${(playersFines?.[p.email] || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex flex-col gap-2">

              <select
                className="w-full border rounded px-2 py-2 text-black"
                onChange={(e) =>
                  setSelectedDrinkForUser(prev => ({
                    ...prev,
                    [p.email]: e.target.value
                  }))
                }
                value={selectedDrinkForUser[p.email] || ""}
              >
                <option value="" disabled>Select drink</option>
                {drinkStock.map(drink => (
                  <option key={drink.name} value={drink.name}>
                    {drink.name} - ${drink.price?.toFixed(2) ?? "0.00"}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAdminPurchase(p.email)}
                  className="bg-blue-600 text-white px-3 py-2 rounded whitespace-nowrap"
                  disabled={!selectedDrinkForUser[p.email]?.trim()}
                >
                  Redeem
                </button>

                <button
                  onClick={() => deactivateUser(p.email)}
                  className="bg-yellow-600 text-white px-3 py-2 rounded"
                >
                  Deactivate
                </button>
              </div>

            </div>

          </div>
        );
      })}

    {/* TOTALS */}
    <div className="bg-gray-100 rounded-xl p-4 text-slate-900">
      <div className="font-semibold mb-1">Totals</div>
      <div className="text-sm">
        Balance: <strong>${totalBalanceAll.toFixed(2)}</strong>
      </div>
      <div className="text-sm">
        Spend: <strong>${totalAlcoholSpendAll.toFixed(2)}</strong>
      </div>
      <div className="text-sm">
        Fines: <strong>${totalFinesAll.toFixed(2)}</strong>
      </div>
    </div>

  </div>
) : (
  <div className="overflow-visible">
  <table className="w-full table-fixed text-sm sm:text-base border mb-6">
      <thead>
        <tr>
          <th className="w-[15%] p-2 text-left">Name</th>
<th className="w-[24%] p-2 text-left">Email</th>
<th className="w-[10%] p-2 text-right whitespace-nowrap">Balance</th>
<th className="w-[11%] p-2 text-right whitespace-nowrap">Spend</th>
<th className="w-[9%] p-2 text-right whitespace-nowrap">Fines</th>
<th className="w-[31%] p-2 text-left">Actions</th>
        </tr>
      </thead>
      <tbody>
        {players
          .filter(p => p.status === "approved")
          .filter(p =>
            (p.name && p.name.includes(search)) ||
            `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
            p.email.includes(search)
          )
          .map(p => (
            <tr key={p.email}>
              <td className="p-2 align-top">{p.firstName} {p.lastName}</td>
              <td className="p-2 align-top break-all leading-snug text-xs sm:text-sm">{p.email}</td>
              <td className="p-2 align-top text-right whitespace-nowrap">${balances[p.email]?.balance ?? 0}</td>
              <td className="p-2 align-top text-right whitespace-nowrap">
                ${txs
                  .filter(t =>
                    t.email === p.email &&
                    t.teamId === teamId &&
                    t.clubId === clubId &&
                    isSpendTransaction(t) &&
                    !t.description?.includes("Top-up via Stripe") &&
                    !t.description?.includes("Admin balance update")
                  )
                  .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                  .toFixed(2)}
              </td>
              <td className="p-2 align-top text-right whitespace-nowrap">
                ${playersFines && playersFines[p.email]
                  ? playersFines[p.email].toFixed(2)
                  : "0.00"}
              </td>
              <td className="p-2 align-top">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => deactivateUser(p.email)}
                    className="bg-yellow-600 text-white px-2 py-1 rounded ml-2"
                  >
                    Deactivate
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 items-center mt-2">
                  <select
                    className="border rounded px-2 py-1 text-black"
                    onChange={(e) => setSelectedDrinkForUser(prev => ({ ...prev, [p.email]: e.target.value }))}
                    value={selectedDrinkForUser[p.email] || ""}
                  >
                    <option value="" disabled>Select drink</option>
                    {drinkStock.map(drink => (
                      <option key={drink.name} value={drink.name}>
                        {drink.name} - ${drink.price?.toFixed(2) ?? "0.00"}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => handleAdminPurchase(p.email, selectedDrinkForUser[p.email])}
                    className="bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50"
                    disabled={!selectedDrinkForUser[p.email]?.trim()}
                  >
                    Redeem
                  </button>
                </div>
              </td>
            </tr>
          ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="p-2 text-left" colSpan={2}>Totals (filtered)</td>
          <td className="p-2 text-right whitespace-nowrap">${totalBalanceAll.toFixed(2)}</td>
          <td className="p-2 text-right whitespace-nowrap">${totalAlcoholSpendAll.toFixed(2)}</td>
          <td className="p-2 text-right whitespace-nowrap">${totalFinesAll.toFixed(2)}</td>
          <td className="p-2" />
        </tr>
      </tfoot>
    </table>
  </div>
)}

{/* Weekly Transactions Section */}
<h3 className="text-xl font-bold mt-10 mb-3">📊 This Week's Transactions</h3>
<div className="mb-6">
  <div className="bg-gray-800 text-white border rounded-lg p-4 shadow">
    <div className="flex justify-between items-center mb-4">
      <h4 className="text-lg font-semibold">Recent Activity</h4>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className="text-sm text-gray-300">
          {weeklyTransactions.length} transaction{weeklyTransactions.length !== 1 ? 's' : ''} this week
        </span>
        <button
          onClick={exportTransactions}
          className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm"
        >
          Export Transactions CSV
        </button>
      </div>
    </div>
    
    {weeklyTransactions.length === 0 ? (
      <p className="text-gray-300 text-center py-4">No transactions this week</p>
    ) : (
      isMobile ? (
        <div className="grid gap-2 mb-4">
          {weeklyTransactions.map((tx, index) => (
            <div key={index} className="border border-gray-700 rounded p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">
                  {new Date(tx.createdAt || tx.date).toLocaleDateString('en-AU', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
                <span className={`font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount > 0 ? '+' : ''}${tx.amount?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="mt-1 text-sm break-words">{tx.description || tx.item || 'N/A'}</div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-gray-300 break-all">{tx.email || 'Unknown'}</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  tx.amount > 0
                    ? 'bg-green-600 text-white'
                    : tx.description?.toLowerCase().includes('fine')
                      ? 'bg-red-600 text-white'
                      : 'bg-blue-600 text-white'
                }`}>
                  {tx.amount > 0 ? 'Top-up' : tx.description?.toLowerCase().includes('fine') ? 'Fine' : 'Redeem'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm sm:text-base border mb-6 bg-black text-white">
            <thead>
              <tr className="border-b border-gray-600">
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">User</th>
                <th className="p-2 text-left">Description</th>
                <th className="p-2 text-left">Amount</th>
                <th className="p-2 text-left">Type</th>
              </tr>
            </thead>
            <tbody>
              {weeklyTransactions.map((tx, index) => (
                <tr key={index} className="border-t border-gray-700">
                  <td className="p-2">
                    {new Date(tx.createdAt || tx.date).toLocaleDateString('en-AU', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td className="p-2 break-all">{tx.email || 'Unknown'}</td>
                  <td className="p-2 break-words">{tx.description || tx.item || 'N/A'}</td>
                  <td className={`p-2 font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}${tx.amount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      tx.amount > 0 ? 'bg-green-600 text-white' : tx.description?.toLowerCase().includes('fine') ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {tx.amount > 0 ? 'Top-up' : tx.description?.toLowerCase().includes('fine') ? 'Fine' : 'Redeem'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    )}
    
    {/* Weekly Summary */}
    {weeklyTransactions.length > 0 && (
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-green-100 text-green-800 p-3 rounded">
          <div className="text-sm font-medium">Total Top-ups</div>
          <div className="text-lg font-bold">
            ${weeklyTransactions
              .filter(tx => tx.amount > 0)
              .reduce((sum, tx) => sum + tx.amount, 0)
              .toFixed(2)}
          </div>
        </div>
        <div className="bg-red-100 text-red-800 p-3 rounded">
          <div className="text-sm font-medium">Total Redeems</div>
          <div className="text-lg font-bold">
            ${Math.abs(weeklyTransactions
              .filter(tx => tx.amount < 0 && !tx.description?.toLowerCase().includes('fine'))
              .reduce((sum, tx) => sum + tx.amount, 0))
              .toFixed(2)}
          </div>
        </div>
        <div className="bg-yellow-100 text-yellow-800 p-3 rounded">
          <div className="text-sm font-medium">Total Fines</div>
          <div className="text-lg font-bold">
            ${Math.abs(weeklyTransactions
              .filter(tx => tx.description?.toLowerCase().includes('fine'))
              .reduce((sum, tx) => sum + tx.amount, 0))
              .toFixed(2)}
          </div>
        </div>
      </div>
    )}
  </div>
</div>

{isMobile && (
  <>
    <h3 className="text-xl font-bold mt-10 mb-3">CSV Exports</h3>
    <div className="bg-gray-800 text-white border rounded-lg p-4 shadow mb-6">
      <div className="grid grid-cols-1 gap-3">
        <button
          onClick={handleExportShopPurchases}
          className="w-full bg-blue-700 text-white px-4 py-3 rounded font-semibold"
        >
          Export Bottle Shop CSV
        </button>
        <button
          onClick={handleExportFinesCsv}
          className="w-full bg-purple-600 text-white px-4 py-3 rounded font-semibold"
        >
          Export Fines CSV
        </button>
      </div>
    </div>
  </>
)}

{!isMobile && (
    <>

      <h3 className="text-xl font-bold mt-10 mb-3">🍻 Manage Stock</h3>
      <div className="bg-gray-800 text-white border rounded-lg p-4 shadow mb-6">
      <div className="overflow-x-auto">
<table className="w-full border text-base mb-4 bg-black text-white">
  <thead>
    <tr>
      <th className="p-2 text-left">Name</th>
      <th className="p-2 text-left">Price</th>
      <th className="p-2 text-left">Qty</th>
      <th className="p-2 text-left">Type</th>
      <th className="p-2 text-left">Spend</th>
      <th className="p-2 text-left">Actions</th>
    </tr>
  </thead>
  <tbody>
    {drinkStock.map(drink => (
  <tr key={drink.name} className="border-t">
    <td className="p-2">{drink.name}</td>
    <td className="p-2">
      <input
        type="number"
        step="0.01"
        min="0"
        value={drink.editPrice ?? drink.price}
        className="p-2 border rounded bg-white text-black w-24"
        onChange={(e) => {
          const newPrice = parseFloat(e.target.value);
          setDrinkStock(prev =>
            prev.map(d => d.name === drink.name ? { ...d, editPrice: newPrice } : d)
          );
        }}
      />
    </td>
    <td className="p-2">
      <input
        type="number"
        min="0"
        value={drink.editStock ?? drink.stock}
        className="p-2 border rounded bg-white text-black w-24"
        onChange={(e) => {
          const newStock = parseInt(e.target.value);
          setDrinkStock(prev =>
            prev.map(d => d.name === drink.name ? { ...d, editStock: newStock } : d)
          );
        }}
      />
    </td>
    <td className="p-2">{drink.type}</td>
    <td className="p-2">
  ${Number(spendByDrink[drink.name] || 0).toFixed(2)}
    </td>
    <td className="p-2">
      <button
        className="bg-blue-600 text-white px-3 py-1 rounded"
        onClick={async () => {
  const price = Number(drink.editPrice ?? drink.price);
  const stock = Number(drink.editStock ?? drink.stock);

  if (!drink.name || isNaN(price) || isNaN(stock)) {
    alert("❌ Invalid name, price, or stock");
    return;
  }

  const payload = {
    name: drink.name,
    price,
    stock,
    teamId,
    clubId
  };

  try {
    const res = await secureFetch(`${API_BASE}/drinks/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (res.success) {
      const updated = await secureFetch(`${API_BASE}/drinks?teamId=${teamId}&clubId=${clubId}`);
      setDrinkStock(updated);
      alert(`✅ Updated ${drink.name}`);
      trackEvent("admin_update_drink", {
        item_name: drink.name,
        value: price,
        currency: "AUD"
      });
    } else {
      alert(`❌ Failed to update ${drink.name}`);
    }
  } catch (err) {
    console.error("❌ Update error:", err);
    alert("Server error updating drink");
  }
}}
      >
        Update
      </button>
      <button
    className="bg-red-600 text-white px-3 py-1 rounded"
    onClick={() => handleDeleteDrink(drink.name)}
    title="Delete this drink (requires stock = 0 if safe mode)"
  >
    Delete
  </button>
    </td>
  </tr>
))}
  </tbody>
  <tfoot>
    <tr>
      <td className="p-2" colSpan={4}>Total Spend</td>
      <td className="p-2">${grandTotalBottleShopSpent.toFixed(2)}</td>
      <td className="p-2" />
    </tr>
  </tfoot>
</table>
</div>
<div className="mb-4">
  <h4 className="font-semibold mb-2">Add New Drink</h4>
  <div className="grid grid-cols-1 md:grid-cols-[minmax(160px,2fr)_7rem_6rem_minmax(110px,1fr)_auto] gap-2">
    <input type="text" placeholder="Name" value={newDrink.name} onChange={e => setNewDrink({ ...newDrink, name: e.target.value })}
  className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white"/>
<input type="number" placeholder="Price" value={newDrink.price} onChange={e => setNewDrink({ ...newDrink, price: e.target.value })} className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white" />
    <input type="number" placeholder="Stock" value={newDrink.stock} onChange={e => setNewDrink({ ...newDrink, stock: e.target.value })} className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white" />
    <select value={newDrink.type} onChange={e => setNewDrink({ ...newDrink, type: e.target.value })} className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white">
      <option>Beer</option>
      <option>Spirit</option>
      <option>Wine</option>
      <option>Bubbles</option>
      <option>Mixer</option>
    </select>
    <button onClick={handleAddDrink} className="px-4 py-2 bg-green-600 text-white rounded whitespace-nowrap">Add Drink</button>
  </div>
</div>
<h4 className="font-semibold mt-6 mb-2">Log Bottle Shop Purchase</h4>
<div className="grid grid-cols-1 md:grid-cols-[minmax(160px,2fr)_5.5rem_7.5rem_minmax(140px,1.5fr)_auto] gap-2 mb-4">
  <select
    className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white"
    value={shopPurchase.drinkName}
    onChange={(e) => setShopPurchase({ ...shopPurchase, drinkName: e.target.value })}
  >
    <option value="">Select drink</option>
    {drinkStock.map(d => (
      <option key={d.name} value={d.name}>{d.name}</option>
    ))}
  </select>
  <input
    type="number"
    placeholder="Qty"
    value={shopPurchase.quantity}
    onChange={(e) => setShopPurchase({ ...shopPurchase, quantity: e.target.value })}
    className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white"
  />
  <input
  type="number"
  placeholder="Total Cost ($)"
  value={shopPurchase.totalCost || ""}
  onChange={(e) =>
    setShopPurchase({ ...shopPurchase, totalCost: e.target.value })
  }
  className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white"
/>
  <input
  type="text"
  placeholder="Shop Name"
  value={shopPurchase.shopName || ""}
  onChange={(e) =>
    setShopPurchase({ ...shopPurchase, shopName: e.target.value })
  }
  className="w-full min-w-0 p-2 border rounded bg-white dark:bg-gray-800 text-black dark:text-white"
/>
  <button
    onClick={handleBottleShopPurchase}
    className="px-4 py-2 bg-green-600 text-white rounded whitespace-nowrap"
  >
    Log Purchase
  </button>
</div>

<div className="overflow-x-auto mt-4">
  <h4 className="font-semibold mb-2">Bottle Shop Purchases</h4>
  <table className="w-full text-sm sm:text-base border mb-4 bg-black text-white">
    <thead>
      <tr>
        <th className="p-2 text-left">Date</th>
        <th className="p-2 text-left">Drink</th>
        <th className="p-2 text-left">Qty</th>
        <th className="p-2 text-left">Total Cost</th>
        <th className="p-2 text-left">Shop</th>
        <th className="p-2 text-left">Logged By</th>
      </tr>
    </thead>
    <tbody>
      {(shopPurchases || []).map((p, i) => (
        <tr key={`${p.date}-${p.drinkName}-${i}`} className="border-t">
          <td className="p-2">{formatSydneyDate(p.date || p.createdAt)}</td>
          <td className="p-2">{p.drinkName}</td>
          <td className="p-2">{p.quantity}</td>
          <td className="p-2">{typeof p.totalCost === 'number' ? `$${p.totalCost.toFixed(2)}` : '-'}</td>
          <td className="p-2">{p.shopName || '-'}</td>
          <td className="p-2">{p.addedByEmail || p.email || '-'}</td>
        </tr>
      ))}
    </tbody>
  
  <tfoot>
  <tr>
    <td className="p-2" colSpan={3}>Total</td>
    <td className="p-2">${totalBottleShopCostShown.toFixed(2)}</td>
    <td className="p-2" />
    <td className="p-2" />
  </tr>
</tfoot>
</table>
<button
  onClick={handleExportShopPurchases}
  className="mt-2 bg-blue-700 text-white px-4 py-2 rounded"
>
  Export Bottle Shop Log to CSV
</button>
</div>
</div>
<>
<h3 className="text-xl font-bold mt-10 mb-3"> Manage Fines</h3>
<section className="mb-8 border rounded p-4 shadow">
        <h4 className="text-lg font-semibold mt-6 mb-2">📋 Apply Fine to Player</h4>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const email = e.target.email.value;
            const fineId = e.target.fineTypeId.value;
            const selectedFine = fineTypes.find(f => f.id === fineId);
            const amount = selectedFine?.amount ?? 0;
            const description = selectedFine?.description ?? "Manual fine";
            const teamId = localStorage.getItem("beercard_teamId");
            const clubId = localStorage.getItem("beercard_clubId");

            if (!email || !fineId) {
              alert("Please complete all fields.");
              return;
            }

            try {
              const res = await secureFetch(`${API_BASE}/fines/add`, {
                method: "POST",
                body: JSON.stringify({ email, amount, description, fineId, teamId, clubId })
              });
              alert("✅ Fine applied!");
              trackEvent("admin_apply_fine", {
                value: amount,
                currency: "AUD",
                fine_type: description
              });
              e.target.reset();
              await loadDashboardData();
            } catch (err) {
              console.error("❌ Failed to apply fine", err);
              alert("❌ Error applying fine");
            }
          }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4"
        >
          <div>
            <label className="block mb-1 font-medium">Player</label>
            <select name="email" className="w-full border rounded px-2 py-1 text-black" required>
              <option value="">Select a player</option>
              {players
                .filter(p => p.status === "approved")
                .map(p => (
                  <option key={p.email} value={p.email}>
                    {p.firstName} {p.lastName} ({p.email})
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-medium">Fine Type</label>
            <select name="fineTypeId" className="w-full border rounded px-2 py-1 text-black" required>
              <option value="">Select a fine</option>
              {fineTypes.map(f => (
                <option key={f.id} value={f.id}>
                  {f.description} (${f.amount})
                </option>
              ))}
            </select>
          </div>

          <div>
    <label className="block mb-1 font-medium">Round</label>
    <select name="round" className="w-full border rounded px-2 py-1 text-black">
      <option value="">(optional)</option>
      {Array.from({ length: 100 }, (_, i) => i + 1).map(n => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  </div>

          <div className="sm:col-span-4">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2">
              Apply Fine
            </button>
          </div>
        </form>
      
  <h4 className="text-lg font-semibold mt-6 mb-2">Update Existing Fine Amount</h4>
  <form
    onSubmit={async (e) => {
      e.preventDefault();
      const fineId = e.target.overrideFineId.value;
      const fine = fineTypes.find(f => f.id === fineId);
      const description = fine?.description;
      const newAmount = parseFloat(e.target.overrideAmount.value);
      const teamId = localStorage.getItem("beercard_teamId");
      const clubId = localStorage.getItem("beercard_clubId");

      if (!description || isNaN(newAmount)) {
        alert("❌ Please select a fine and enter a new amount.");
        return;
      }

      try {
        const res = await secureFetch(`${API_BASE}/fine-types/add`, {
          method: "POST",
          body: JSON.stringify({ description, amount: newAmount, teamId, clubId, type: "override" })
        });

        if (res.success) {
          alert("✅ Fine amount overridden.");
          trackEvent("admin_override_fine_amount", {
            value: newAmount,
            currency: "AUD",
            fine_type: description
          });
          await loadFineTypes();
          e.target.reset();
        } else {
          alert("❌ Failed to override fine");
        }
      } catch (err) {
        console.error("❌ Error overriding fine type:", err);
        alert("❌ Server error");
      }
    }}
    className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4"
  >
    <select name="overrideFineId" className="border rounded px-2 py-1 text-black" required>
  <option value="">Select a fine to override</option>
  {fineTypes.map(f => (
    <option key={f.id} value={f.id}>
      {`${f.description} ($${f.amount})`}
    </option>
  ))}
</select>
    <input name="overrideAmount" type="number" step="0.01" placeholder="New Amount ($)" className="border rounded px-2 py-1 text-black" required />
    <button type="submit" className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700">
      Override Amount
    </button>
  </form>

  <h3 className="text-xl font-bold mt-10 mb-3"> Add New Fine Types</h3>

  <form
    onSubmit={async (e) => {
      e.preventDefault();
      const description = e.target.description.value.trim();
      const amount = parseFloat(e.target.amount.value);
      const teamId = localStorage.getItem("beercard_teamId");
      const clubId = localStorage.getItem("beercard_clubId");

      if (!description || isNaN(amount) || !teamId || !clubId) {
        alert("❌ Missing required fields");
        return;
      }

      try {
        const res = await secureFetch(`${API_BASE}/fine-types/add`, {
          method: "POST",
          body: JSON.stringify({ description, amount, teamId, clubId, type: "custom" })
        });

        if (res.success) {
          alert("✅ Fine type added");
          trackEvent("admin_add_fine_type", {
            value: amount,
            currency: "AUD"
          });
          e.target.reset();
          await loadFineTypes();
        } else {
          alert("❌ Failed to add fine type");
        }
      } catch (err) {
        console.error("❌ Failed to add fine type", err);
        alert("❌ Error adding fine type");
      }
    }}
    className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
    <input name="description" type="text" placeholder="Description" className="border rounded px-2 py-1 text-black" required />
    <input name="amount" type="number" placeholder="Amount ($)" step="0.01" className="border rounded px-2 py-1 text-black" required />
    <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
      Add Fine Type
    </button>
  </form>

  <h4 className="text-lg font-semibold mt-6 mb-2">Custom/Overridden Fines</h4>
  <table className="w-full text-sm border">
    <thead>
      <tr><th className="p-2 text-left">Description</th><th>Amount</th></tr>
    </thead>
    <tbody>
      {fineTypes
        .filter(f => f.teamId !== "ALL" || f.clubId !== "ALL")
        .map(f => (
          <tr key={f.id}>
            <td className="p-2">{f.description}</td>
            <td className="p-2">${f.amount}</td>
          </tr>
      ))}
    </tbody>
  </table>


</section>
<button
    onClick={handleExportFinesCsv}
    className="bg-purple-600 text-white px-4 py-2 rounded"
  >
    Export Fines to CSV
  </button>

</>
</>
)}
      </div>
    </div>
    
  );
}
